import { useState, useEffect, useRef } from "react";

import { useParams } from "react-router-dom";

import { TUTRED_CLIENT_HOSTNAME } from "@/constants";
import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { UserRole } from "@/types";

import LoadingPage from "../LoadingPage";

import LessonCompleteView from "./features/LessonCompleteView";
import RateLessonDialog from "./features/RateLessonDialog";

const CompletedLesson = () => {
  const [loading, setLoading] = useState(true);
  const { lesson } = useLesson();
  const { authenticatedAxios, userRole } = useFirebaseContext();
  const { lessonId } = useParams();
  const [existingReview, setExistingReview] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const autoOpenedReviewRef = useRef(false);

  const isGuest = lesson?.type === "guest";

  const actions = {
    [UserRole.Student]: {
      title: "Thanks for learning with us!",
      description: lesson?.type === "guest"
        ? "We hope you enjoyed your guest lesson. Sign up to continue your learning journey!"
        : "We hope you enjoyed your lesson. Come back soon to keep growing!",
      buttonText: lesson?.type === "guest" ? "Join Tutred" : "Back to Lessons",
      buttonLink: lesson?.type === "guest" ? "/signup?role=student" : `/student/lessons/past/${lessonId}`,
    },
    [UserRole.Tutor]: {
      title: "Thanks for teaching with us!",
      description: lesson?.type === "guest"
        ? "We hope you enjoyed teaching this guest lesson!"
        : "We hope you enjoyed your lesson. Don't forget to assign homework for your student!",
      buttonText: lesson?.type === "guest" ? "Go to Tutred" : "Go to Homework",
      buttonLink: lesson?.type === "guest" ? "/tutor/home" : `/tutor/homework/${lessonId}`,
    },
  };

  useEffect(() => {
    if (lesson?.isCancelled) {
      window.location.href = `${TUTRED_CLIENT_HOSTNAME}`;
      return;
    }
    if (isGuest) {
      setLoading(false);
      return;
    }
    authenticatedAxios.get(`/reviews/${lessonId}`).then((res) => {
      setExistingReview(res.data.review !== null);
      setLoading(false);
    });
  }, [lessonId, authenticatedAxios, lesson?.isCancelled, isGuest]);

  const needsReview = !isGuest && !existingReview;

  useEffect(() => {
    if (!loading && needsReview && !autoOpenedReviewRef.current) {
      autoOpenedReviewRef.current = true;
      setReviewDialogOpen(true);
    }
  }, [loading, needsReview]);

  if (loading) {
    return <LoadingPage />;
  }

  const primaryHref = `${TUTRED_CLIENT_HOSTNAME}${actions[userRole].buttonLink}`;

  return (
    <>
      <LessonCompleteView
        title={actions[userRole].title}
        description={actions[userRole].description}
        primaryLabel={actions[userRole].buttonText}
        primaryHref={primaryHref}
        showRateLesson={needsReview}
        onRateLesson={
          needsReview ? () => setReviewDialogOpen(true) : undefined
        }
      />

      {needsReview ? (
        <RateLessonDialog
          open={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
          lessonId={lessonId || ""}
          onSubmitted={() => setExistingReview(true)}
        />
      ) : null}
    </>
  );
};

export default CompletedLesson;
